import type { Component, VNode } from "vue";

export class MenuEntry {
  private _subItems?: Array<MenuEntry>;

  component?: Component | VNode;
  icon?: Component | VNode;
  text: string;
  key: string;
  /* Used on the right side of the item, eg. for shortcuts. Can not be used with a submenu. */
  subText?: string;
  callback?: (event?: MouseEvent) => void | Promise<void> | PromiseLike<void>;
  hasSeparator: boolean;
  disabled: boolean = false;

  constructor(
    text: string,
    key?: string,
    callback?: (event?: MouseEvent) => void | Promise<void> | PromiseLike<void>,
    icon?: Component | VNode,
    hasSeparator?: boolean,
  ) {
    this.text = text;
    this.key = key ?? text;
    this.callback = callback;
    this.icon = icon;
    this.hasSeparator = hasSeparator ?? false;
  }

  addSubItem(item: MenuEntry): void {
    this._subItems = this._subItems || new Array<MenuEntry>();
    this._subItems.push(item);
  }

  get subItems(): MenuEntry[] | undefined {
    return this._subItems;
  }

  static getMenuEntries<T>(
    items: T[],
    itemTextProperty?: keyof T,
    getText?: (item: T) => string,
    itemKeyProperty?: keyof T,
    getKey?: (item: T) => string,
    itemDisabledProperty?: keyof T,
    checkDisabled?: (item: T) => boolean,
  ): MenuEntry[] {
    const entries = new Array<MenuEntry>();
    items.forEach((item) => {
      const entry = new MenuEntry(
        this.calculateText(item, itemTextProperty, getText),
        this.calculateKey(item, itemKeyProperty, getKey),
      );
      entry.disabled = this.calculateDisabled(item, itemDisabledProperty, checkDisabled);
      if (typeof item === "object" && item !== null && "icon" in item) {
        entry.icon = item.icon as Component | VNode;
      }
      entries.push(entry);
    });
    return entries;
  }

  static calculateDisabled<T>(
    item: T,
    itemDisabledProperty?: keyof T,
    checkDisabled?: (item: T) => boolean,
  ): boolean {
    if (checkDisabled) {
      const disabled = checkDisabled(item);
      if (typeof disabled === "boolean") {
        return disabled;
      } else {
        console.warn("Custom disabled check function is invalid.");
      }
    } else if (
      typeof item === "object" &&
      item !== null &&
      itemDisabledProperty &&
      itemDisabledProperty in item &&
      typeof item[itemDisabledProperty] === "boolean"
    ) {
      return item[itemDisabledProperty];
    }
    return false;
  }

  static calculateKey<T>(
    item: T,
    itemKeyProperty?: keyof T,
    getKey?: (item: T) => string,
  ): string | undefined {
    if (getKey) {
      return getKey(item);
    }
    if (
      typeof item === "object" &&
      item !== null &&
      itemKeyProperty &&
      itemKeyProperty in item
    ) {
      return item[itemKeyProperty]?.toString() ?? "";
    }
    return undefined;
  }

  static calculateText<T>(
    item: T,
    itemTextProperty?: keyof T,
    getText?: (item: T) => string,
  ): string {
    if (getText) {
      return getText(item);
    }
    if (
      typeof item === "object" &&
      item !== null &&
      itemTextProperty &&
      itemTextProperty in item
    ) {
      return item[itemTextProperty]?.toString() ?? "";
    }
    return item?.toString() ?? "";
  }
}
